import useProfile from '~/apps/profile/use-profile';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSmartSendOnboardingProgress from './hooks/use-onboarding-progress';
import SmartSendSetup from './setup';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '../../lib/consts/RoutingConstants';
import useSmartSendVersionFeature from '~/apps/profile/use-smartsend-version-feature';
import { continueAccountLinking } from './components/continue-account-linking';
import { AchievementType } from '../../lib/context/ProfileContext';
import { SmartSendVersions } from './hooks/use-smartsend-version';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { EventSections } from '../../lib/events/EventLogger';
import useOnLoad from '~/routes/use-on-load';
import Loading from '~/components/loading/Loading';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { usePendingPlanDecision } from '~/apps/subscriptions/use-pending-plan-decision';
import { markSubscriptionSeenInOnboarding } from '~/apps/subscriptions/subscription-seen-in-onboarding';

type props = {
  isXs: boolean;
  onPageLoad: () => void;
};

//http://localhost:3000/my-account/accounts?IntegrationSlug=ebay&IntegrationType=Marketplace&Success=True&Action=Link&AccountStatus=Active
const SmartSendSetupProcess = ({ isXs, onPageLoad }: props) => {
  const { track } = useSmartSendAnalytics();
  const { goTo } = useGoto();
  const [justLinked, setJustLinked] = useState<string | null>(null);
  const [justFailed, setJustFailed] = useState<string | null>(null);
  const { data, markAsDone, initialise, addOutstandingActions } = useProfile();
  const { setPreferredSmartSendVersion } = useSmartSendVersionFeature();
  const { setSmartSendOnboardingProgress } = useSmartSendOnboardingProgress();
  // Whether the plan they arrived wanting differs from the one they hold - see
  // use-pending-plan-decision.ts. Only a positively-known difference redirects.
  const planDecision = usePendingPlanDecision();

  // The Subscribe step renders off this same decision (see ./setup), so seeing `show` here means
  // they are being shown it. Recorded now because completing the step clears the pending plan.
  useEffect(() => {
    if (planDecision.kind === 'show') {
      markSubscriptionSeenInOnboarding();
    }
  }, [planDecision]);

  const goToMyPlan = useCallback(() => {
    markSubscriptionSeenInOnboarding();
    goTo(RoutingConstants.MyPlan);
  }, [goTo]);

  const markAsDoneProxy = useCallback(
    (
      keys: AchievementType[],
      outstandingActionsToAdd: string[],
      outstandingActionsToRemove: string[],
      meta: { [key: string]: any }
    ) => {
      // If they've just linked an account, set that state so we can show the correct screen
      if (keys.includes('SmartSendActionAccountLinked') && outstandingActionsToRemove.length === 1) {
        setJustLinked(outstandingActionsToRemove[0]);
      }

      return markAsDone(keys, outstandingActionsToAdd, outstandingActionsToRemove, meta);
    },
    [markAsDone]
  );

  // Completing onboarding ends in a navigation that lands whenever its requests return, so a second
  // call would later pull the customer back to Orders from wherever they had gone. Only the first
  // call does anything; leaving this page unmounts it, so a later visit starts afresh.
  const sendNowStarted = useRef(false);
  const sendNow = useCallback(async () => {
    if (sendNowStarted.current) {
      return;
    }
    sendNowStarted.current = true;

    markAsDoneProxy(['SmartSendSetupCompleteScreen', 'SmartSendSignup'], [], [], {});
    setSmartSendOnboardingProgress('completed');
    await setPreferredSmartSendVersion(SmartSendVersions.SmarterSending);
    track({
      type: 'smartsend_setup_complete',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.Onboarding]),
        ...data.meta,
        achievements: data.achievements.filter((a) => a.value).map((a) => a.key)
      }
    });
    // They arrived wanting a plan other than the one they hold, and the subscribe step was skipped
    // because they are already a subscriber. Onboarding is done, so now is the moment to offer the
    // change - My Plan lays out every plan beside the one they are on.
    if (planDecision.kind === 'skip' && planDecision.redirectToMyPlan) {
      goToMyPlan();
      return;
    }

    goTo(RoutingConstants.SmartSend);
  }, [
    goTo,
    goToMyPlan,
    setSmartSendOnboardingProgress,
    setPreferredSmartSendVersion,
    markAsDoneProxy,
    track,
    data.meta,
    data.achievements,
    planDecision
  ]);

  // Load History for onboarding
  useOnLoad(() => {
    const load = async () => {
      console.log('Checking Smart Send Onboarding status');
      const res = await initialise();
      const completedAchievements = res?.achievements.filter((x) => x.value === true).map((x) => x.key) ?? [];
      const alreadyOnboarded =
        res.userSkipped ||
        completedAchievements.includes('SmartSendActionFirstParcelSent') ||
        completedAchievements.includes('SmartSendSetupCompleteScreen');

      // An account-linking OAuth return is handled regardless of onboarding state. Previously this
      // was only checked in the "not yet onboarded" branch below, so an already-onboarded customer
      // returning from linking a marketplace account was bounced to the dashboard before this ever
      // ran, silently dropping their link result.
      const integrationJustLinked = continueAccountLinking();
      if (integrationJustLinked?.integration) {
        if (integrationJustLinked.success) {
          markAsDoneProxy(
            ['SmartSendSetupAccountScreen', 'SmartSendActionAccountLinked'],
            [],
            [integrationJustLinked.integration],
            {}
          );
          setJustLinked(integrationJustLinked.integration);
        } else {
          markAsDoneProxy(['SmartSendSetupAccountScreen'], [integrationJustLinked.integration], [], {});
          setJustFailed(integrationJustLinked.integration);
        }
        return;
      }

      if (alreadyOnboarded) {
        // Onboarding already completed or skipped, and nothing above needed handling first.
        console.log('Smart Send Onboarding already completed or skipped');
        setSmartSendOnboardingProgress('completed');
        await setPreferredSmartSendVersion(SmartSendVersions.SmarterSending);

        // A pending plan sends them to My Plan to act on it - but not when they already hold it.
        // `pending` (entitlements still settling) is treated as "offer it": the plan is real and
        // this path has no second chance to decide, whereas My Plan costs them nothing to land on -
        // it shows the plans either way.
        if (planDecision.kind === 'pending' || (planDecision.kind === 'skip' && planDecision.redirectToMyPlan)) {
          console.log('Already onboarded with a pending plan - sending to My Plan');
          goToMyPlan();
          return;
        }
        if (planDecision.kind === 'show') {
          console.log('Already onboarded with a pending plan - sending to My Plan to choose');
          goToMyPlan();
          return;
        }

        goTo(RoutingConstants.SmartSend);
        return;
      }

      // Continuation of the onboarding process
      console.log('Starting or continuing Smart Send Onboarding');
      setSmartSendOnboardingProgress('started');
      onPageLoad();
    };
    load();
  });

  if (!data) {
    return <Loading />;
  }

  return (
    <SmartSendSetup
      loading={data.loading}
      isXs={isXs}
      sendNow={sendNow}
      data={data}
      markAsDone={markAsDoneProxy}
      justLinked={justLinked}
      justFailed={justFailed}
      markAsTodo={addOutstandingActions}
    />
  );
};

export default SmartSendSetupProcess;
